/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import clsx from 'clsx';

import { choicesToList } from '../../../algorithm/question_utils';
import { CALCULATOR_METAQUESTION_QUESTION_INDEX } from '../../../algorithm/runAlgorithm';
import styles from './SelectKeysSummaryTable.module.scss';
import { SelectedButtonInfo } from '../../../algorithm/types';
import { useAllUserSelectionsFromAllMessages } from '../../useHooks';
import { PearlUser } from '../../../Login/userSlice';

interface SelectKeysSummaryTableProps {
  showOnlyMessageIndex?: number;
  messageIndex: number;
}

/**
 * Shows the selected buttons in a table, for the swimlane and calculator summary.
 * And summary on the leaf page (MultipleChoice page with no buttons or next link)
 */
export function SelectKeysSummaryTable({ showOnlyMessageIndex, messageIndex }: SelectKeysSummaryTableProps) {
  const selectedKeys = useAllUserSelectionsFromAllMessages(messageIndex, showOnlyMessageIndex);
  return <SelectKeysSummaryTableInner showOnlyMessageIndex={showOnlyMessageIndex} selectedKeys={selectedKeys} />;
}

type SelectKeysSummaryTableInnerProps = {
  showOnlyMessageIndex?: number;
  selectedKeys: { [key: string]: SelectedButtonInfo } | undefined;
  className?: string;
  pearlUserInfo?: PearlUser | undefined;
};

export function SelectKeysSummaryTableInner({
  showOnlyMessageIndex,
  selectedKeys,
  className,
}: SelectKeysSummaryTableInnerProps) {
  const mergedList = selectedKeys ? choicesToList(selectedKeys, true) : undefined;
  if (!selectedKeys) return null;
  if (!mergedList || mergedList?.length <= 0 || mergedList === undefined) return null;

  return (
    <>
      <p className={styles.summaryHeading}>Selection Summary:</p>
      <table className={clsx(className, styles.footer)}>
        <tbody>
          {Object.values(mergedList)
            // This should be already filtered and sorted, so don't add any filtering or sorting here but
            // only in the choicesToList function
            .map((selected, index) =>
              selected.questionIndex! === CALCULATOR_METAQUESTION_QUESTION_INDEX && showOnlyMessageIndex != null ? (
                <tr key={index} className={styles.score}>
                  <td>Score:</td>
                  <th>{selected!.value}</th>
                </tr>
              ) : selected!.summarytext.keyStr ? (
                <tr
                  key={index}
                  className={clsx({
                    [styles.calcScoreRow as string]: selected.questionIndex! === CALCULATOR_METAQUESTION_QUESTION_INDEX,
                  })}
                >
                  <td>{selected!.summarytext.keyStr}:</td>
                  <th>
                    {selected!.summarytext.valueStr}
                    {/* do not show . for the main calculator score, like PHQ 9: score = 8.0*/}
                  </th>
                </tr>
              ) : null
            )}
        </tbody>
      </table>
    </>
  );
}
